import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { ASSETS } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Label } from "../components/bits";
import { subscribe } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const field = "w-full border border-white/15 bg-ink px-4 py-4 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-gold";
const perks = [
  "First access to product drops & new arrivals",
  "Invitations to community events & wellness experiences",
  "Patient education, guidance, and seasonal offers",
];

export default function SignUp() {
  useSeo("/signup");
  const [form, setForm] = useState({ name: "", email: "", interest: "General Updates" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      await subscribe(form);
      setDone(true);
      toast.success("You're on the list — welcome to Higher Ground DC.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="signup-page">
      <section className="relative min-h-screen overflow-hidden pt-32">
        <div className="absolute inset-0">
          <img src={ASSETS.canWoman} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/85" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-16 px-6 py-16 lg:grid-cols-2 lg:px-12 lg:py-28">
          <div>
            <Reveal><Label>Join the Community</Label></Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-5 font-display text-4xl font-light uppercase leading-tight tracking-[0.06em] text-white md:text-5xl lg:text-6xl">
                Sign Up for Higher Ground DC
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-lg leading-relaxed text-white/65">
                Get event updates, product drops, and patient education delivered to your inbox.
                Elevated, never noisy.
              </p>
            </Reveal>
            <ul className="mt-8 space-y-3">
              {perks.map((p, i) => (
                <Reveal key={p} delay={0.2 + i * 0.06}>
                  <li className="flex items-start gap-3 text-white/70">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" /> {p}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-ink-2/90 p-8 backdrop-blur-xl md:p-10" data-testid="signup-form">
              {done ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="signup-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
                    <Check className="h-8 w-8 text-gold" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-light uppercase tracking-[0.1em] text-white">You're In</h2>
                  <p className="mt-3 max-w-xs text-white/55">Welcome to Higher Ground DC. Keep an eye on your inbox for what's next.</p>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <h2 className="font-display text-2xl font-light uppercase tracking-[0.1em] text-white">Get Updates</h2>
                  <div className="mt-8 space-y-4">
                    <input className={field} placeholder="Full name (optional)" value={form.name} onChange={set("name")} data-testid="signup-name" />
                    <input className={field} type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="signup-email" />
                    <select className={field} value={form.interest} onChange={set("interest")} data-testid="signup-interest">
                      <option>General Updates</option>
                      <option>Events & Community</option>
                      <option>New Product Drops</option>
                      <option>Wellness & Education</option>
                    </select>
                  </div>
                  <button type="submit" disabled={loading} data-testid="signup-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne disabled:opacity-60">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign Me Up"}
                  </button>
                  <p className="mt-4 text-center text-xs text-white/40">You must be 21+ to subscribe. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
